// Urgentie-lanes voor het Dagstart-scherm.
//
// De oude Airtable-indeling groepeerde op PrioNr. Dat vertelt je welke taak belangrijk
// gevonden werd, niet welke taak vanochtend besproken moet worden — en dat is precies
// waar de dagstart over gaat. Deze indeling zet de vraag "wat is blijven liggen" bovenaan
// en laat prioriteit terugkomen als kleur en sorteervolgorde binnen een lane.
//
// Puur en zonder React, zodat de regels testbaar zijn: dit is de plek waar de meeste
// randgevallen zitten (taak zonder startdatum, deadline zonder taakdatum, weekend).

import { addDays, formatDateISO, getMonday, parseISODate } from './weekUtils';
import { isDagstaatTaak } from './taakStatus';

export const LANES = {
  DRAAIDAGEN: 'DRAAIDAGEN',
  AFSPRAKEN: 'AFSPRAKEN',
  ACHTERSTALLIG: 'ACHTERSTALLIG',
  VANDAAG: 'VANDAAG',
  DEZE_WEEK: 'DEZE WEEK',
  LATER: 'LATER',
  ONGEPLAND: 'ONGEPLAND',
};

/** Volgorde op het scherm: wat aandacht vraagt eerst. */
export const LANE_VOLGORDE = [
  LANES.DRAAIDAGEN,   // de dag zelf: een draaidag bepaalt de rest (Raijmond, 15 sep)
  LANES.AFSPRAKEN,
  LANES.ACHTERSTALLIG,
  LANES.VANDAAG,
  LANES.DEZE_WEEK,
  LANES.LATER,
  LANES.ONGEPLAND,
];

export const LANE_KLEUR = {
  [LANES.DRAAIDAGEN]: '#f97316',
  [LANES.AFSPRAKEN]: '#8b5cf6',
  [LANES.ACHTERSTALLIG]: '#ef4444',
  [LANES.VANDAAG]: '#0ea5e9',
  [LANES.DEZE_WEEK]: '#22c55e',
  [LANES.LATER]: '#94a3b8',
  [LANES.ONGEPLAND]: '#f59e0b',
};

/**
 * Lanes die dichtgeklapt beginnen. ONGEPLAND zijn taken zonder startdatum: op één
 * project stonden er 37 tegelijk — alle deliverables van een grote serie, netjes
 * aangemaakt maar nog niet ingepland. Dat is werkvoorraad voor de planner, geen
 * onderwerp voor de ochtendronde. Het aantal in de kop blijft zichtbaar, dus het
 * gesprek gaat niet vanzelf weg; het duwt alleen het werk van vandaag niet weg.
 *
 * LATER om dezelfde reden: bij Henk stonden 31 LinkedIn-captions-taken voor de
 * komende maanden onder de dagstart — niet boeiend voor de ochtendronde. De kop
 * met het aantal blijft; openklappen kan altijd.
 */
export const STANDAARD_INGEKLAPT = [LANES.LATER, LANES.ONGEPLAND];

/**
 * Deadlinetypes die als harde belofte tellen. De vier waarden in Airtable zijn
 * Hard, Klant, Zacht en Intern; alleen de eerste twee zijn aan iemand buiten het
 * team toegezegd en verdienen dus de achterstallig-lane als ze verlopen.
 */
const HARDE_DEADLINES = new Set(['Hard', 'Klant']);

export const isHardeDeadline = (deadlineType) => HARDE_DEADLINES.has(String(deadlineType || '').trim());

/** Alleen het datumdeel; Airtable geeft deadlines soms als datetime terug. */
const datumDeel = (waarde) => {
  const s = String(waarde || '').trim();
  return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : '';
};

const isWeekend = (iso) => {
  const d = parseISODate(iso);
  if (!d) return false;
  const dag = d.getDay();
  return dag === 0 || dag === 6;
};

/**
 * Werkdagen (ma-vr) tussen twee ISO-datums, inclusief. Een einddatum vóór de startdatum
 * komt in de data voor (typefout in Airtable) en levert hier gewoon de startdag op —
 * beter één blokje op de verkeerde dag dan een taak die van het scherm verdwijnt.
 */
export function werkdagenInBereik(startISO, eindISO) {
  const start = datumDeel(startISO);
  if (!start) return [];
  const eind = datumDeel(eindISO) || start;
  const laatste = eind < start ? start : eind;

  const dagen = [];
  let d = parseISODate(start);
  const grens = parseISODate(laatste);
  if (!d || !grens) return [];
  // Harde bovengrens: kapotte data (eind in 2099) mag geen oneindige lus geven.
  let veiligheid = 0;
  while (d <= grens && veiligheid++ < 400) {
    const iso = formatDateISO(d);
    if (!isWeekend(iso)) dagen.push(iso);
    d = addDays(d, 1);
  }
  // Een taak die volledig in het weekend valt hoort toch ergens te staan.
  return dagen.length ? dagen : [start];
}

/**
 * Doorlopende werkdag-index: hoeveel werkdagen ligt `iso` na `ankerISO`.
 * Nodig om te slepen — een blok een kolom naar rechts op vrijdag moet op maandag
 * landen, niet op zaterdag.
 */
export function werkdagIndex(iso, ankerISO) {
  const doel = parseISODate(datumDeel(iso));
  const anker = parseISODate(datumDeel(ankerISO));
  if (!doel || !anker) return 0;

  const vooruit = doel >= anker;
  const [van, tot] = vooruit ? [anker, doel] : [doel, anker];
  let n = 0;
  let d = van;
  let veiligheid = 0;
  while (d < tot && veiligheid++ < 2000) {
    d = addDays(d, 1);
    if (!isWeekend(formatDateISO(d))) n += 1;
  }
  return vooruit ? n : -n;
}

/** `iso` n werkdagen verschoven (weekenden overgeslagen). */
export function verschuifWerkdagen(iso, n) {
  const start = parseISODate(datumDeel(iso));
  if (!start) return datumDeel(iso);
  if (n === 0) return formatDateISO(start);

  const stap = n > 0 ? 1 : -1;
  let over = Math.abs(n);
  let d = start;
  let veiligheid = 0;
  while (over > 0 && veiligheid++ < 2000) {
    d = addDays(d, stap);
    if (!isWeekend(formatDateISO(d))) over -= 1;
  }
  return formatDateISO(d);
}

/**
 * In welke lane hoort deze taak?
 *
 * `vandaagISO` is bewust een parameter en géén `new Date()` in deze functie: de lanes
 * worden afgeleid van de échte vandaag, niet van het zichtbare venster. Blader je naar
 * volgende week, dan mag "achterstallig" niet ineens iets anders betekenen — en het
 * maakt de functie testbaar.
 *
 * @param taak        { type, prio, startDate, endDate, deadline, deadlineType }
 * @param vandaagISO  'YYYY-MM-DD'
 * @param uitgesloten Set met statussen die niet meetellen (uit useCapacityRules)
 * @returns lane-naam, of null als de taak niet thuishoort in het overzicht
 */
/** PrioNr-waarden waarmee een taak klaar is (spiegel van core/taken.AFGEROND_PRIONRS). */
export const KLAAR_PRIOS = new Set(['Afgerond', 'Opgestuurd', 'Gearchiveerd']);

export function laneVan(taak, vandaagISO, uitgesloten = new Set()) {
  if (!taak) return null;

  // Afgerond staat twee keer in Airtable: als checkbox én als waarde in PrioNr. Wie er
  // maar één checkt, houdt taken op het scherm die elders al weg zijn.
  if (taak.isComplete === true) return null;
  // Opgestuurd/Gearchiveerd zijn ook klaar (core/taken.AFGEROND_PRIONRS): na "Opgestuurd" op
  // het blok verdwijnt de taak meteen uit de lanes, ook als de regels die status niet uitsluiten.
  if (KLAAR_PRIOS.has(taak.prio)) return null;
  if (uitgesloten.has(taak.prio) || uitgesloten.has(taak.status)) return null;

  if (taak.type === 'Afspraak') return LANES.AFSPRAKEN;

  const vandaag = datumDeel(vandaagISO);
  const start = datumDeel(taak.startDate);

  if (!start) return LANES.ONGEPLAND;

  const eind = datumDeel(taak.endDate) || start;
  const echtEind = eind < start ? start : eind;

  // Draaidagen bovenaan, boven de afspraken: de draaidag ís de dag (Raijmond, 15 sep). Ook
  // een verstreken draaidag blijft een draaidag op zijn eigen datum: nooit achterstallig, en
  // hij hoeft niet afgevinkt te worden (Joris, 3 nov 2026). Vóór het venster valt hij weg in
  // zichtbareTaken.
  if (isDraaidag(taak)) return LANES.DRAAIDAGEN;

  if (echtEind < vandaag) return LANES.ACHTERSTALLIG;

  // Een verstreken deadline op werk dat nog in de toekomst staat krijgt bewust géén eigen
  // lane. Dat is geprobeerd en het werkte niet: op de echte data (13 aug 2026) waren dat er
  // teambreed 213, want een deadline in Airtable wordt zelden bijgesteld als het werk
  // opnieuw wordt ingepland. Zo'n lane vult zich dus met administratieve ruis en trekt de
  // taak weg van de dag waarop hij daadwerkelijk staat. De taak blijft nu gewoon op zijn
  // eigen datum staan, met een klein vlaggetje op het blok (zie TaskBlock).

  if (start <= vandaag) return LANES.VANDAAG;

  const zondag = formatDateISO(addDays(getMonday(parseISODate(vandaag)), 6));
  if (start <= zondag) return LANES.DEZE_WEEK;

  return LANES.LATER;
}

/**
 * Valt deze afspraak op een werkdag die in beeld staat?
 *
 * Een afspraak hoort alleen op zijn eigen dag. Taken die in het weekend vallen schuiven
 * bewust door naar de eerstvolgende werkdag (anders verdwijnen ze), maar voor een afspraak
 * is dat fout: de zaterdagse verjaardag en het zondagse etentje stonden zo op maandag in de
 * dagstart (Michel, 20 okt). Het venster begint op maandag bij vrijdag, dus het weekend zit
 * er altijd middenin. Hetzelfde voor een afspraak die vóór het venster ligt: die werd op de
 * eerste kolom vastgepind, alsof hij gisteren was.
 *
 * Een afspraak die over middernacht loopt (start vrijdag 22:00) heeft als datum zijn
 * startdag en blijft dus op vrijdag staan.
 */
export function afspraakInVenster(taak, vensterStart = '', vensterEind = '') {
  const start = datumDeel(taak?.startDate);
  if (!start) return false;
  const eind = datumDeel(taak?.endDate) || start;
  const van = datumDeel(vensterStart);
  const tot = datumDeel(vensterEind);
  const laag = van && van > start ? van : start;
  const hoog = tot && tot < (eind < start ? start : eind) ? tot : (eind < start ? start : eind);
  if (laag > hoog) return false;
  // werkdagenInBereik valt bij alleen-weekend terug op de startdag; hier niet.
  return werkdagenInBereik(laag, hoog).some((d) => !isWeekend(d));
}

/** Grens voor "kleine taak": een kwartier. */
export const KLEIN_UREN = 0.25;

/**
 * Een kleine taak (bv. "Licenties controleren", 0,2 u): werk met een ingevuld aantal uren
 * van hooguit een kwartier. Afspraken en draaidagen nooit; taken zonder uren ook niet (die
 * krijgen een inschatting en zijn dus niet "klein").
 */
export function isKleineTaak(taak) {
  if (!taak || taak.type === 'Afspraak' || isDraaidag(taak)) return false;
  const u = Number(taak.hours);
  return Number.isFinite(u) && u > 0 && u <= KLEIN_UREN + 1e-9;
}

const isDraaidag = (taak) => taak?.type === 'Draaidag' || taak?.prio === 'Draaidag' || taak?.status === 'Draaidag';

const isVanallesuurtje = (taak) => taak?.type === 'Vanallesuurtje' || taak?.prio === 'Vanallesuurtje';

/** Een vrije dag of vrij/intern-blok: een dagstáát, geen werk dat af moet. */
const isVrijeDag = (taak) => ['Vrije dag', 'Vrij/intern']
  .includes(taak?.type) || ['Vrije dag', 'Vrij/intern'].includes(taak?.prio);

/**
 * Wat de dagstart níet laat zien. Drie soorten ruis:
 *
 * · **Vanallesuurtjes** — het terugkerende blok van een uur voor losse kleine klussen.
 *   Het staat elke dag op ieders tijdlijn en het haalt het gesprek nooit.
 * · **Taken die pas ná het venster beginnen.** Die werden op de laatste kolom getekend
 *   (anders zouden ze wegvallen), waardoor het leek alsof er vrijdag werk lag dat in
 *   werkelijkheid pas over drie weken start. Geldt voor élke lane: ook een taak met een
 *   verstreken deadline die pas in oktober gepland staat hoort niet op de laatste dag
 *   opgestapeld te worden — hij staat gewoon waar hij hoort, buiten beeld.
 * · **Vrije dagen die voorbij zijn.** Die belandden in ACHTERSTALLIG, en achterstallig
 *   zijn op een vrije dag is onzin: je loopt niet achter op vrij zijn. Een vrije dag
 *   die nog komt of vandaag is blijft wél staan — dán is het gespreksstof.
 * · **Draaidagen van vóór het venster** (3 nov 2026). Een draaidag is nooit achterstallig;
 *   binnen het venster staat hij op zijn eigen dag, daarvoor hoort hij niet op de eerste
 *   kolom vastgepind te worden.
 *
 * · **Achterstallig bij een venster in de toekomst.** Blader je vooruit (het venster begint
 *   ná vandaag), dan hoort achterstallig werk niet op de eerste kolom van die week te staan:
 *   het is een zaak van vandaag (wiki-feedback 26 sep). Het staat bij "vandaag" in beeld.
 * · **Kleine taken (≤ 15 min)**, alleen als de gebruiker dat aanzet (`verbergKlein`): om te
 *   zien waar nog ruimte is zonder de licentie-controletjes van een kwartier.
 *
 * De capaciteitsberekening loopt bewust over álle taken, ook de verborgen: het cijfer
 * moet kloppen, ook als het blok niet in beeld staat. Daarom geeft deze functie terug
 * wát er wegviel — dan kan het scherm dat melden in plaats van stil te verbergen.
 *
 * @returns { zichtbaar: taken[], verborgen: { vanallesuurtje, buitenVenster, vrijeDag, draaidag, achterstallig, klein } }
 */
export function zichtbareTaken(taken, vandaagISO, uitgesloten = new Set(), opties = {}) {
  const { vensterEind = '', vensterStart = '', toonVanallesuurtje = false, verbergKlein = false } = opties;
  const grens = datumDeel(vensterEind);
  const inToekomst = !!datumDeel(vensterStart) && datumDeel(vensterStart) > datumDeel(vandaagISO);
  const zichtbaar = [];
  const begin = datumDeel(vensterStart);
  const verborgen = { vanallesuurtje: 0, buitenVenster: 0, vrijeDag: 0, draaidag: 0, achterstallig: 0, klein: 0 };

  (taken || []).forEach((taak) => {
    const lane = laneVan(taak, vandaagISO, uitgesloten);
    if (!lane) return;
    // Afspraken alleen op hun eigen werkdag: niet doorschuiven vanuit het weekend en niet
    // vastpinnen op de rand van het venster (zie afspraakInVenster).
    if (lane === LANES.AFSPRAKEN && !afspraakInVenster(taak, vensterStart, vensterEind)) return;
    if (lane === LANES.ACHTERSTALLIG && isVrijeDag(taak)) { verborgen.vrijeDag += 1; return; }
    if (lane === LANES.DRAAIDAGEN && begin) {
      const s0 = datumDeel(taak.startDate);
      const e0 = datumDeel(taak.endDate) || s0;
      if ((e0 < s0 ? s0 : e0) < begin) { verborgen.draaidag += 1; return; }
    }
    if (lane === LANES.ACHTERSTALLIG && inToekomst) { verborgen.achterstallig += 1; return; }
    if (!toonVanallesuurtje && isVanallesuurtje(taak)) { verborgen.vanallesuurtje += 1; return; }
    if (verbergKlein && isKleineTaak(taak)) { verborgen.klein += 1; return; }
    if (grens && datumDeel(taak.startDate) > grens) {
      verborgen.buitenVenster += 1;
      return;
    }
    zichtbaar.push(taak);
  });

  return { zichtbaar, verborgen };
}

/** Hoeveel dagen de deadline verstreken is (0 of minder = nog niet). */
export function dagenOverDeadline(taak, vandaagISO) {
  // Draaidag of vrije dag: een verstreken deadline zegt niets, voorbij is voorbij (3 nov 2026).
  if (isDagstaatTaak(taak)) return 0;
  const deadline = datumDeel(taak?.deadline);
  const vandaag = datumDeel(vandaagISO);
  if (!deadline || !vandaag || deadline >= vandaag) return 0;
  return Math.abs(werkdagIndex(deadline, vandaag));
}

// Prioriteit bepaalt de volgorde bínnen een lane. 'Deadline' en Prio 1 bovenaan,
// parkeerstanden onderaan.
const PRIO_RANG = {
  'Deadline': 0, 'Prio 1': 1, 'Prio 2': 2, 'Prio 3': 3, 'Prio 4': 4,
  'Draaidag': 5, 'Afspraak': 6, 'Geen prio': 7,
  'Wacht op feedback': 8, 'Nog niet van toepassing': 9, 'Vanallesuurtje': 10,
};

export const prioRang = (prio) => PRIO_RANG[prio] ?? 7;

/**
 * Alle taken van één persoon in lanes, gesorteerd. Lege lanes komen niet terug —
 * een kop zonder inhoud is alleen maar ruis op een gedeeld scherm.
 */
export function verdeelInLanes(taken, vandaagISO, uitgesloten = new Set()) {
  const buckets = new Map(LANE_VOLGORDE.map((l) => [l, []]));

  (taken || []).forEach((taak) => {
    const lane = laneVan(taak, vandaagISO, uitgesloten);
    if (lane) buckets.get(lane).push(taak);
  });

  return LANE_VOLGORDE
    .map((lane) => ({
      lane,
      kleur: LANE_KLEUR[lane],
      taken: buckets.get(lane).sort((a, b) => {
        const p = prioRang(a.prio) - prioRang(b.prio);
        if (p !== 0) return p;
        const d = datumDeel(a.startDate).localeCompare(datumDeel(b.startDate));
        if (d !== 0) return d;
        // Afspraken op dezelfde dag horen op tijd te staan, niet op alfabet: de lane
        // leest dan als je dag. Werk heeft geen tijd en valt terug op de naam.
        const t = String(a.startTime || '').localeCompare(String(b.startTime || ''));
        if (t !== 0) return t;
        return String(a.name || '').localeCompare(String(b.name || ''));
      }),
    }))
    .filter((g) => g.taken.length > 0);
}

// ── Indeling op prioriteit ───────────────────────────────────────────────────
// Naast de urgentie-indeling (achterstallig/vandaag/later) kun je de dagstart ook
// op prioriteit lezen. Uit de wiki-feedback: "kunnen we in plaats van achterstallig,
// vandaag en later op prio sorteren? Dit is duidelijker dan de kleuren."
//
// Afspraken houden hun eigen lane: die hebben geen prioriteit en zouden anders
// allemaal onder "Geen prio" belanden.

export const PRIO_LANE_VOLGORDE = [
  LANES.DRAAIDAGEN,
  LANES.AFSPRAKEN,
  'Deadline',
  'Prio 1',
  'Prio 2',
  'Prio 3',
  'Prio 4',
  'Geen prio',
  'Wacht op feedback',
  'Nog niet van toepassing',
];

/** De lanes waar je een taak naartoe kunt verplaatsen — parkeerstanden incluis. */
export const PRIO_LANES_INSTELBAAR = [
  'Deadline', 'Prio 1', 'Prio 2', 'Prio 3', 'Prio 4', 'Geen prio',
  'Wacht op feedback', 'Nog niet van toepassing',
];

export const PRIO_LANE_KLEUR = {
  [LANES.DRAAIDAGEN]: '#f97316',
  [LANES.AFSPRAKEN]: '#8b5cf6',
  'Deadline': '#dc2626',
  'Prio 1': '#ef4444',
  'Prio 2': '#f97316',
  'Prio 3': '#eab308',
  'Prio 4': '#84cc16',
  'Geen prio': '#3b82f6',
  'Wacht op feedback': '#f59e0b',
  'Nog niet van toepassing': '#94a3b8',
};

/** Standaard dicht: parkeerstanden zijn geen onderwerp voor de ochtendronde. */
export const PRIO_STANDAARD_INGEKLAPT = ['Nog niet van toepassing'];

/**
 * Dezelfde taken, maar gegroepeerd op prioriteit in plaats van op urgentie.
 *
 * De urgentiefilter blijft gelden: een taak die in de urgentie-indeling nergens
 * terechtkomt (bv. buiten het venster) hoort hier ook niet te staan. Daarom lopen
 * we langs `laneVan` en gebruiken we die alleen als zeef.
 */
export function verdeelInPrioLanes(taken, vandaagISO, uitgesloten = new Set()) {
  const buckets = new Map(PRIO_LANE_VOLGORDE.map((l) => [l, []]));

  (taken || []).forEach((taak) => {
    const urgentie = laneVan(taak, vandaagISO, uitgesloten);
    if (!urgentie) return;                       // valt sowieso buiten beeld
    if (urgentie === LANES.AFSPRAKEN || urgentie === LANES.DRAAIDAGEN) {
      buckets.get(urgentie).push(taak);
      return;
    }
    const prio = String(taak.prio || '').trim();
    const lane = buckets.has(prio) ? prio : 'Geen prio';
    buckets.get(lane).push(taak);
  });

  return PRIO_LANE_VOLGORDE
    .map((lane) => ({
      lane,
      kleur: PRIO_LANE_KLEUR[lane],
      taken: buckets.get(lane).sort((a, b) => {
        // Binnen één prioriteit telt de tijd: wat het eerst begint bovenaan.
        const d = datumDeel(a.startDate).localeCompare(datumDeel(b.startDate));
        if (d !== 0) return d;
        const t = String(a.startTime || '').localeCompare(String(b.startTime || ''));
        if (t !== 0) return t;
        return String(a.name || '').localeCompare(String(b.name || ''));
      }),
    }))
    .filter((g) => g.taken.length > 0);
}
